import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { StreamClientProvider } from './contexts/StreamClientContext';
import { ToastProvider } from './contexts/ToastContext';
import { UploadQueueProvider } from './contexts/UploadQueueContext';
import { ThemeProvider } from './contexts/ThemeContext';
import NotificationNavigationBridge from './components/NotificationNavigationBridge';
// Not lazy: it must paint the instant sign-in succeeds, so it ships in the
// main bundle rather than arriving as its own chunk mid-transition.
import StageDoorOverlay from './components/StageDoorOverlay';
import { PageLoader } from './components/ui_elements/LoadingSpinner';
import ErrorBoundary from './components/ui_elements/ErrorBoundary';
import './App.css';
import ProtectedRoute from './components/ProtectedRoute';
import { loginPathFor, RETURN_PARAM, safeReturnPath } from './utils/returnPath';

// Route-level splitting. The dashboard and the unauthenticated pages have no
// overlap, so neither audience should pay for the other: someone landing on
// /login or an activation link no longer downloads the whole admin portal.
const LoginPage = lazy(() => import('./components/LoginPage'));
const DashboardLayout = lazy(() => import('./components/DashboardLayout'));
const ShowProfileWrapper = lazy(() => import('./components/ShowProfileWrapper'));
const ActivatePage = lazy(() => import('./pages/ActivatePage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const StudentSuccessPage = lazy(() => import('./pages/StudentSuccessPage'));
const StaffSuccessPage = lazy(() => import('./pages/StaffSuccessPage'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes (renamed from cacheTime)
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Persist the shows list across hard reloads so the dashboard paints
// instantly from last-known data while React Query revalidates in the
// background. Deliberately scoped to the shows key only - other queries
// hold larger or more sensitive payloads (cast lists, chat credentials)
// that should not sit in localStorage. Logout already clears the cache via
// queryClient.clear(), which the persister mirrors into storage.
persistQueryClient({
  queryClient,
  persister: createSyncStoragePersister({
    storage: window.localStorage,
    key: 'limelight-shows-cache',
  }),
  maxAge: 24 * 60 * 60 * 1000, // drop anything older than a day
  buster: 'shows-v1', // bump to invalidate persisted data after schema changes
  dehydrateOptions: {
    shouldDehydrateQuery: (query) =>
      query.queryKey[0] === 'shows' && query.state.status === 'success',
  },
});

/**
 * /login: the sign-in page, or - once signed in - wherever `?next=` pointed
 * (a same-origin path only; see utils/returnPath), else the dashboard.
 */
const LoginRoute: React.FC<{ isAuthenticated: boolean }> = ({ isAuthenticated }) => {
  const [searchParams] = useSearchParams();
  if (!isAuthenticated) return <LoginPage />;
  return <Navigate to={safeReturnPath(searchParams.get(RETURN_PARAM)) ?? '/'} replace />;
};

/** Signed out on the dashboard: to sign-in, keeping any section in the link. */
const SignedOutRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={loginPathFor(location)} replace />;
};

// Main App Component
const AppContent: React.FC = () => {
  const { user, profile, loading, showStageIntro } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  const isAuthenticated = user && profile;

  return (
    <Router>
      <NotificationNavigationBridge />
      {/* "Setting the stage" - covers the login -> dashboard transition
          after an explicit sign-in, dismissing itself when data is ready */}
      {showStageIntro && <StageDoorOverlay />}
      {/* Every route below is a lazy chunk. PageLoader is the right fallback
          here - unlike the tab-level boundary, this really is a whole-page
          swap, and it matches the loader already shown while auth resolves. */}
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route
          path="/login"
          element={<LoginRoute isAuthenticated={!!isAuthenticated} />}
        />
        <Route 
          path="/activate" 
          element={<ActivatePage />} 
        />
        <Route 
          path="/reset-password" 
          element={<ResetPasswordPage />} 
        />
        <Route
          path="/student-success"
          element={<StudentSuccessPage />}
        />
        <Route
          path="/staff-success"
          element={<StaffSuccessPage />}
        />
        <Route 
          path="/shows/:showId" 
          element={
            <ProtectedRoute>
              <ShowProfileWrapper />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/" 
          element={
            isAuthenticated ? (
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            ) : (
              <SignedOutRedirect />
            )
          } 
        />
        <Route 
          path="*" 
          element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />} 
        />
      </Routes>
      </Suspense>
    </Router>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <AuthProvider>
        <StreamClientProvider>
        <NotificationProvider>
          <ToastProvider>
            <UploadQueueProvider>
              <ErrorBoundary fullScreen fallbackMessage="Something went wrong">
                <AppContent />
              </ErrorBoundary>
            </UploadQueueProvider>
          </ToastProvider>
        </NotificationProvider>
        </StreamClientProvider>
      </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;